import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CheckCircle, Store, TrendingUp, Users, Clock, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  owner_name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  restaurant_name: z.string().min(2, "El nombre del restaurante debe tener al menos 2 caracteres"),
  email: z.string().email("Por favor ingresa un email válido"),
  phone: z.string().min(8, "El teléfono debe tener al menos 8 dígitos"),
  message: z.string().optional(),
});

const benefits = [
  { icon: Store, text: "Sin costo de inscripción" },
  { icon: TrendingUp, text: "Aumenta tus ventas hasta 60%" },
  { icon: Users, text: "Acceso a miles de clientes" },
  { icon: Clock, text: "Activación en 24 horas" },
];

const RegistrationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      owner_name: "",
      restaurant_name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/business/register`, data);
      if (response.data) {
        setIsSuccess(true);
        toast.success("¡Registro exitoso! Te contactaremos pronto.");
        reset();
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Error al registrar. Por favor intenta de nuevo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="registro" className="py-24 bg-muted/50" data-testid="registration-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="uppercase text-xs tracking-[0.2em] font-bold text-primary">
            Únete Ahora
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground mt-4">
            Registra Tu Restaurante
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
            Completa el formulario y nuestro equipo te contactará en menos de 24 horas 
            para comenzar a crecer juntos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="form-container neumorphic rounded-3xl p-8"
          >
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  ¡Registro Exitoso!
                </h3>
                <p className="text-muted-foreground">
                  Te contactaremos muy pronto para comenzar.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="owner_name" className="text-foreground font-medium">
                      Tu Nombre *
                    </Label>
                    <Input
                      id="owner_name"
                      placeholder="Juan Pérez"
                      className="rounded-xl border-border focus:border-primary py-3"
                      data-testid="input-owner-name"
                      {...register("owner_name")}
                    />
                    {errors.owner_name && (
                      <p className="text-destructive text-sm">{errors.owner_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="restaurant_name" className="text-foreground font-medium">
                      Nombre del Restaurante *
                    </Label>
                    <Input
                      id="restaurant_name"
                      placeholder="Tacos El Rey"
                      className="rounded-xl border-border focus:border-primary py-3"
                      data-testid="input-restaurant-name"
                      {...register("restaurant_name")}
                    />
                    {errors.restaurant_name && (
                      <p className="text-destructive text-sm">{errors.restaurant_name.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      className="rounded-xl border-border focus:border-primary py-3"
                      data-testid="input-email"
                      {...register("email")}
                    />
                    {errors.email && (
                      <p className="text-destructive text-sm">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground font-medium">
                      Teléfono *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+57 3226835194"
                      className="rounded-xl border-border focus:border-primary py-3"
                      data-testid="input-phone"
                      {...register("phone")}
                    />
                    {errors.phone && (
                      <p className="text-destructive text-sm">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground font-medium">
                    Mensaje (Opcional)
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Cuéntanos sobre tu negocio..."
                    className="rounded-xl border-border focus:border-primary min-h-[100px]"
                    data-testid="input-message"
                    {...register("message")}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg font-bold transition-all duration-300 hover:scale-[1.02] hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-registration-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Registrar Mi Negocio"
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Al registrarte, aceptas nuestros{" "}
                  <a href="#" className="text-primary hover:underline">
                    Términos y Condiciones
                  </a>
                </p>
              </form>
            )}
          </motion.div>

          {/* Benefits Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="neumorphic rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">
                ¿Por qué unirte a Apedir?
              </h3>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.text}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-primary/5"
                    data-testid={`registration-benefit-${index + 1}`}
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <benefit.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium text-foreground">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="neumorphic rounded-3xl p-8">
              <h3 className="text-xl font-bold text-foreground mb-4">
                ¿Tienes dudas?
              </h3>
              <p className="text-muted-foreground mb-4">
                Nuestro equipo está listo para ayudarte.
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-foreground">
                  <span className="font-medium">Email:</span> negocios@apedir.com
                </p>
                <p className="text-foreground">
                  <span className="font-medium">WhatsApp:</span> +57 3226835194
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RegistrationForm;
